
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { AdminUser, UserProfileResponse } from "./types";
import { UserRole } from "@/types/strategy-types";
import { deleteUserViaEdge } from "@/utils/deleteUserUtils";

export const useUserCrud = (
  setUsers: React.Dispatch<React.SetStateAction<AdminUser[]>>,
  sortUsers: (users: AdminUser[]) => AdminUser[]
) => {
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const loadUsers = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        toast({
          title: "Error",
          description: "Not authenticated",
          variant: "destructive",
        });
        return;
      }

      setLoading(true);

      // Use our secure function to get user data including emails
      const { data: userData, error: userError } = await supabase
        .rpc('get_users_with_email');

      if (userError) {
        console.error('Error fetching users data:', userError);
        toast({
          title: "Error",
          description: "Failed to load users",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // Get referral relationships data
      const { data: referralData, error: referralError } = await supabase
        .from('referral_relationships')
        .select('*');

      if (referralError) {
        console.error('Error fetching referral relationships:', referralError);
        toast({
          title: "Warning",
          description: "Failed to load referral data",
          variant: "destructive",
        });
        // Continue with user data anyway
      }

      console.log('Referral relationships loaded:', referralData?.length || 0);

      // Get referral codes for users
      const { data: referralCodes, error: referralCodesError } = await supabase
        .from('referral_codes')
        .select('*');

      if (referralCodesError) {
        console.error('Error fetching referral codes:', referralCodesError);
      }

      // Create a map of referrer IDs to their referral relationships
      const referralMap = new Map();

      if (referralData) {
        referralData.forEach(rel => {
          const relationships = referralMap.get(rel.referrer_id) || [];
          relationships.push({
            referred_id: rel.referred_id,
            paid_status: rel.paid_status || false
          });
          referralMap.set(rel.referrer_id, relationships);
        });
      }

      // Create a map of user IDs to referral codes
      const referralCodeMap = new Map();

      if (referralCodes) {
        referralCodes.forEach(rc => {
          referralCodeMap.set(rc.user_id, rc.code);
        });
      }

      console.log('Users data loaded:', userData?.length || 0);
      console.log('Users with referrals:', referralMap.size);

      // Format users with all data
      const formattedUsers: AdminUser[] = userData.map((user) => {
        const referralRelationships = referralMap.get(user.id) || [];
        const referralCode = referralCodeMap.get(user.id);

        return {
          id: user.id,
          email: user.email || 'Email unavailable',
          role: user.role,
          created_at: user.created_at || new Date().toISOString(),
          roleId: user.role_id,
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          provider_id: user.provider_id || undefined,
          referral_code: referralCode,
          referral_relationships: referralRelationships
        };
      });

      // Log some stats for debugging
      const usersWithReferrals = formattedUsers.filter(u => u.referral_relationships?.length > 0);
      console.log(`Users with referrals after formatting: ${usersWithReferrals.length}`);

      if (usersWithReferrals.length > 0) {
        console.log("Sample referrer:", {
          id: usersWithReferrals[0].id,
          referral_code: usersWithReferrals[0].referral_code,
          relationships: usersWithReferrals[0].referral_relationships
        });
      }

      setUsers(sortUsers(formattedUsers));
      setLoading(false);
    } catch (error) {
      console.error('Error loading users:', error);
      toast({
        title: "Error",
        description: "Failed to load users",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  const updateUserRole = async (roleId: string, newRole: UserRole) => {
    try {
      await supabase.rpc('update_user_role', {
        role_id: roleId,
        new_role: newRole
      });

      toast({
        title: "Success",
        description: "User role updated successfully",
      });

      await loadUsers();
    } catch (error) {
      console.error('Error updating user role:', error);
      toast({
        title: "Error",
        description: "Failed to update user role",
        variant: "destructive",
      });
    }
  };

  const updateUserName = async (userId: string, firstName: string, lastName: string) => {
    try {
      // Update names in user_roles table
      const { error } = await supabase
        .from('user_roles')
        .update({
          first_name: firstName,
          last_name: lastName
        })
        .eq('user_id', userId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "User name updated successfully",
      });

      await loadUsers();
    } catch (error) {
      console.error('Error updating user name:', error);
      toast({
        title: "Error",
        description: "Failed to update user name",
        variant: "destructive",
      });
    }
  };

  const deleteUser = async (userId: string) => {
    try {
      // Get the current user's session for authorization
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");
      /*
           // Call our new edge function instead of the RPC
           const { error } = await supabase.functions.invoke('delete-user', {
             body: { userId },
             headers: {
               Authorization: `Bearer ${session.access_token}`
             }
           });
     
           if (error) {
             console.error('Function error:', error);
             throw new Error(error.message || 'Failed to delete user');
           }*/

      let res = await deleteUserViaEdge(userId);
      if (res) {
        toast({
          title: "Success",
          description: "User deleted successfully",
        });

        await loadUsers();
      } else {
        toast({
          title: "Error",
          description: "Failed to delete user",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete user",
        variant: "destructive",
      });
    }
  };

  return {
    loading,
    loadUsers,
    updateUserRole,
    updateUserName,
    deleteUser
  };
};
